/**
 * @fileoverview Pure field-discovery rules for the generate-precedents
 * wizard's Review Fields step.
 *
 * Purpose:
 * - Hold the three decisions the wizard makes about a placeholder key that
 *   are easy to get wrong and hard to test inside a 10k-line route:
 *   1. which body placeholders are registered as manual inputs;
 *   2. what label / input type a registered key renders with when it has no
 *      canonical, custom or extracted definition;
 *   3. whether a key shared by several selected templates is rendered once
 *      in Common Fields or once per document card.
 *
 * Why: MOR-2314 — a placeholder inserted into a letter body after
 * extraction (`{{accountants_initials}}`) has no definition anywhere, so the
 * wizard silently dropped it while the day-one Edit Fields panel (which scans
 * the body) still showed it. The rules below keep the two in step.
 *
 * Used by:
 * - `_layout.jobs.$jobId.generate-precedents.tsx`
 */

import type { Doc } from "@cvx/_generated/dataModel";
import type {
  CanonicalField,
  FieldType,
} from "@cvx/settings/precedents/canonicalFields";
import {
  contactFieldLabel,
  isContactFieldKey,
} from "@cvx/jobs/precedents/contactFields";
import { collectPlaceholderKeysFromStoredTemplate } from "@/ui/composed/editor/html-utils";
import { preferTemplateFieldLabel } from "./templateFieldLabels";
import { isManualPlaceholderKey, manualFieldFallback } from "./manualFieldKeys";

/**
 * Placeholder keys in a template body that the wizard must offer as manual
 * inputs even though no `extractedFields` row describes them.
 *
 * Scans the CURRENT body only. The shared scanner's `extractedContent`
 * fallback is deliberately not used here: that text is the frozen artefact
 * of the original extraction, so consulting it once the body has lost its
 * last placeholder would resurrect a manual key the user just deleted.
 * Every key returned is therefore something the user can still see in the
 * letter. Families with their own resolver (contact bindings, firm
 * references, collection items, the signature block) are left to their
 * existing discovery paths, as are keys the template hides.
 *
 * Returns an empty list for PDF-overlay templates, whose `templateHtml` is
 * an extraction artefact rather than a render input (MOR-790).
 */
export function collectManualBodyFieldKeys(
  template: Pick<
    Partial<Doc<"precedentTemplates">>,
    "templateHtml" | "extractedContent"
  >,
  options: {
    isPdfFillTemplate: boolean;
    hiddenKeys?: ReadonlySet<string>;
  },
): string[] {
  // `template.extractedContent` is intentionally never read — see above.
  const { templateHtml } = template;
  if (!templateHtml || options.isPdfFillTemplate) return [];
  const hidden = options.hiddenKeys ?? new Set<string>();
  return Array.from(collectPlaceholderKeysFromStoredTemplate(templateHtml))
    .filter((key) => !hidden.has(key))
    .filter(isManualPlaceholderKey);
}

/** One row of `precedentTemplates.extractedFields`, as stored. */
type ExtractedFieldRow = NonNullable<
  Doc<"precedentTemplates">["extractedFields"]
>[number];

/** The parts of a template's own `extractedFields` row a card renders from. */
export type ExtractedFieldMeta = Pick<
  ExtractedFieldRow,
  "label" | "fieldType" | "description"
>;

/**
 * Every input type a canonical field can declare. `satisfies` keeps this in
 * step with the `FieldType` union at compile time.
 */
const FIELD_TYPES = {
  text: true,
  date: true,
  address: true,
  signature: true,
  checkbox: true,
  number: true,
  email: true,
  phone: true,
} as const satisfies Record<FieldType, true>;

function isFieldType(value: string): value is FieldType {
  return Object.prototype.hasOwnProperty.call(FIELD_TYPES, value);
}

/**
 * Boundary between the stored row and the typed card: `extractedFields.
 * fieldType` is a free string written by extraction, while the review input
 * only distinguishes date, checkbox and signature and renders anything else
 * as text — so an unknown value narrows to "text" rather than being cast.
 */
function extractedFieldType(value: string): FieldType {
  return isFieldType(value) ? value : "text";
}

/**
 * Resolve the label / input type a per-document card renders for `fieldKey`.
 *
 * Resolution order mirrors the day-one Edit Fields panel:
 *   canonical or org custom definition (with the template's own label
 *   overlaid) → the template's `extractedFields` row → contact-binding label
 *   → title-cased text input for any other manual placeholder.
 *
 * Returns `undefined` when the key must not render as a canonical-style
 * input: keys the card already renders as extras (checkbox-group bundles)
 * and keys belonging to a family with its own control (firm references,
 * collection items, the signature block).
 */
export function resolvePerDocumentFieldMeta(
  fieldKey: string,
  sources: {
    /** Canonical or org custom definition, when one exists. */
    definition?: CanonicalField;
    /** The template's own `extractedFields` row, when one exists. */
    extracted?: ExtractedFieldMeta;
    /** Keys this card already renders through its extras list. */
    extraKeys?: ReadonlySet<string>;
  },
): CanonicalField | undefined {
  const { definition, extracted } = sources;
  if (definition) {
    // The template's own wording wins over the canonical default so the
    // input is named after the placeholder the user can see in the document.
    return preferTemplateFieldLabel(definition, extracted);
  }
  if (extracted) {
    return {
      key: fieldKey,
      label: extracted.label,
      description: extracted.description,
      fieldType: extractedFieldType(extracted.fieldType),
    };
  }
  if (isContactFieldKey(fieldKey)) {
    return {
      key: fieldKey,
      label: contactFieldLabel(fieldKey) ?? fieldKey,
      fieldType: "text",
    };
  }
  if (sources.extraKeys?.has(fieldKey)) return undefined;
  if (!isManualPlaceholderKey(fieldKey)) return undefined;
  return manualFieldFallback(fieldKey);
}

/**
 * Split registered field keys into the Common Fields card and per-document
 * cards.
 *
 * A key goes to Common Fields when it belongs to a default category, or when
 * two or more selected templates use it AND it has a canonical or org custom
 * definition. The Common Fields card renders from category definitions, so
 * a definition-less key (a body-only manual placeholder shared by two
 * letters) promoted there would vanish from every card; it stays
 * per-document instead, once in each card that uses it. Values are keyed by
 * field, so one entry still fills every document.
 */
export function partitionWizardFieldKeys(
  fieldToTemplates: ReadonlyMap<string, ReadonlySet<string>>,
  rules: {
    hasDefinition: (fieldKey: string) => boolean;
    isDefaultCategoryKey: (fieldKey: string) => boolean;
    isAlwaysPerDocument: (fieldKey: string) => boolean;
  },
): {
  commonFieldKeys: Set<string>;
  formOnlyByTemplate: Map<string, Set<string>>;
} {
  const common = new Set<string>();
  const formOnly = new Map<string, Set<string>>();
  for (const [fieldKey, templates] of fieldToTemplates) {
    const shared =
      templates.size >= 2 &&
      rules.hasDefinition(fieldKey) &&
      !rules.isAlwaysPerDocument(fieldKey);
    if (shared || rules.isDefaultCategoryKey(fieldKey)) {
      common.add(fieldKey);
      continue;
    }
    for (const templateKey of templates) {
      const keys = formOnly.get(templateKey) ?? new Set<string>();
      keys.add(fieldKey);
      formOnly.set(templateKey, keys);
    }
  }
  return { commonFieldKeys: common, formOnlyByTemplate: formOnly };
}
